import gql from "graphql-tag";
import type * as React from "react";

import {RenderState, useRenderState} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {useFastlyQuery} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {useRegionOverride} from "@repo/data-region-override";

import type {
    footerDomainsQuery,
    footerDomainsQueryVariables,
} from "./__graphql-types__/footerDomainsQuery.ts";
import type {DomainObject} from "./types.ts";

export const DATA_QUERY: GqlOperation<
    footerDomainsQuery,
    footerDomainsQueryVariables
> = gqlOp(gql`
    query footerDomainsQuery($region: String) {
        footerDomains(region: $region) {
            translatedTitle
            href
        }
    }
`);

/**
 * Result mapped to the component props
 */
export type FooterDomainsQueryResult = {
    loading: boolean;
    error?: string;
    domains: Array<DomainObject>;
};

type Props = {
    children: (result: FooterDomainsQueryResult) => React.ReactNode;
};

/**
 * A query component in charge of getting the list of domains used in the
 * courses list in the footer
 */
const FooterDomainsQuery = (props: Props): React.ReactElement => {
    const renderState = useRenderState();
    const regionResult = useRegionOverride();

    // We don't want to access RequestInfo.kaRegion through useRegionOverride
    // during SSR so that we have a greater cache hit rate. We'll skip the query
    // during SSR and initial render and show as loading instead.
    const options =
        renderState === RenderState.Initial || regionResult.status !== "success"
            ? {skip: true}
            : {
                  variables: {
                      region: regionResult.regionCode,
                  },
              };

    const result = useFastlyQuery(DATA_QUERY, options);
    // @ts-expect-error - TS2322 - Type 'ReactNode' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
    return props.children(mapDataToResult(result));
};

/**
 * Converts the query result to the component props
 * @param {Result<Data>} result - The data received from the server
 *
 * @returns {FooterDomainsQueryResult} The data ready to be consumed by the
 * child component
 */
const mapDataToResult = (
    result: Result<footerDomainsQuery>,
): FooterDomainsQueryResult => {
    if (result.status === "success" && result.data.footerDomains != null) {
        return {
            loading: false,
            domains: result.data.footerDomains.map((domain) => ({
                translatedTitle: domain?.translatedTitle ?? "",
                href: domain?.href ?? "",
                slug: "",
                children: [],
            })),
        };
    }

    if (result.status === "error") {
        return {
            loading: false,
            error: result.error.message,
            domains: [],
        };
    }

    return {
        loading: result.status === "loading" || result.status === "no-data",
        error: result.status === "aborted" ? "Query aborted" : undefined,
        domains: [],
    };
};

export default FooterDomainsQuery;
